import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PersonalIcon from '@dls/assets/icons/Personal';
import ShopIcon from '@dls/assets/icons/Shop';
import * as actions from '@/actions';
import ContentReviewer from '@/components/ContentReviewer';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import EmpowerHeader from '@/components/Header/EmpowerHeader';
import IdleStatusChecker from '@/components/IdleStatusChecker';
import Popover from '@/components/Popover';
import useFooter from '@/hooks/useFooter';
import useMediaQuery from '@/hooks/useMediaQuery';
import useTermConditionAndDataProtection from '@/hooks/useTermConditionAndDataProtection';
import useTranslation from '@/hooks/useTranslation';
import { checkPdpaEnabled, isEmpowerFlow } from '@/helpers/common';
import Emitter from '@/helpers/EventEmitter';
import navigate from '@/helpers/navigation';
import { EVENT_MENU_CLICK, EVENT_MODAL_NAVIGATION, PREVENT_NAVIGATE_LIST } from '@/config/menu';
import { Container, AccountText } from './styles';
import { StyledList, StyledText } from '@/components/Header/styles';
import { ILayout } from '@/components/Layout/models/LayoutModel';
import { IContent } from '@/components/Addon/AddonPopup/models/AddonPopupModel';
import AnimationLoader from '../AnimationLoader';

const iconProps = {
  size: 25,
  color: 'white'
};

const selector = ({ auth }: ILayout) => ({
  isLoggedIn: auth.isLoggedIn,
  currentAccount: auth.currentAccount,
  auth,
  isTokenLoading: auth.isTokenLoading,
  isStaffLogin: auth.isStaffLogin,
});

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoggedIn, currentAccount, auth, isTokenLoading, isStaffLogin } = useSelector(selector);
  const { isMaxSM } = useMediaQuery();
  const { isMaxXS } = useMediaQuery();
  const { copyrightText, footerItemList } = useFooter();
  const pageData = useTermConditionAndDataProtection(currentAccount.custtype);
  const [content, setContent] = useState<IContent | null>(null);

  const checkPdpa = checkPdpaEnabled(auth);

  const footerMenu = footerItemList?.filter(item => ((!isStaffLogin && item.link !== 'staff-plan-tc') || isStaffLogin)).map((item) => {
    return {
      ...item,
      onClick: () =>
        setContent({
          title: item.text,
          data: pageData[item.link]
        })
    };
  });

  const onMenuClick = (menu) => () => {
    if (PREVENT_NAVIGATE_LIST.includes(window.location.pathname)) {
      Emitter.emit(EVENT_MODAL_NAVIGATION, { menu });
    } else {
      navigate(menu.pathname);
      Emitter.emit(EVENT_MENU_CLICK, { nextPathName: menu.pathname });
    }
  };

  const handleLogout = () => {
    dispatch(actions.logout.request());
  };

  const onClosed = () => {
    setContent(null);
  }

  const enableBrnDetail = !checkPdpa && !isMaxXS

  return (
    <Container>
      {isLoggedIn && <IdleStatusChecker isLoggedIn={isLoggedIn} />}
      {isEmpowerFlow()
        ? <EmpowerHeader>{isTokenLoading ? <AnimationLoader/> : children}</EmpowerHeader>
        : <>
          <Header
            checkPdpa={checkPdpa}
            isAuth={isLoggedIn}
            isMobileView={isMaxSM}
            onMenuClick={onMenuClick}
            rightItems={[
              enableBrnDetail ? (
                <AccountText type="smallBody">{currentAccount?.company}</AccountText>
              ) : null,
              enableBrnDetail ? (
                <AccountText type="smallBody">{`${currentAccount?.custtype}${currentAccount?.brn}`}</AccountText>
              ) : null,
              <>
                <PersonalIcon {...iconProps} />
                <Popover align="right">
                  <StyledList>
                    {!isEmpowerFlow() && <li
                      data-testid="account-profile"
                      onClick={onMenuClick({ pathname: 'ACCOUNT' })}
                    >
                      <StyledText>{t('HEADER_MENU_ACCOUNT')}</StyledText>
                    </li>}
                    <li data-testid="user-guide" onClick={onMenuClick({ pathname: 'USER_GUIDE' })}>
                      <StyledText>{t('HEADER_MENU_USER_GUIDE')}</StyledText>
                    </li>
                    <li data-testid="logout" onClick={handleLogout}>
                      <StyledText>{t('HEADER_MENU_LOGOUT')}</StyledText>
                    </li>
                  </StyledList>
                </Popover>
              </>,
              <a key={'carts'} data-testid="carts" onClick={onMenuClick({ pathname: 'CART' })}>
                <ShopIcon {...iconProps} />
              </a>
            ]}
          />
          {isTokenLoading ? <AnimationLoader /> : children}
          <Footer
            isMobileView={isMaxSM}
            copyrightText={copyrightText}
            menuItems={footerMenu}
          />
        </>
      }
      {content && (
        <ContentReviewer
          data={content.data}
          title={content.title}
          visible={true}
          onClose={onClosed}
        />
      )}
    </Container>
  );
};

export default Layout;
